import { useMemo } from 'react';
import { t } from '@lingui/macro';
import get from 'lodash/get';
import lowerCase from 'lodash/lowerCase';
import isEmpty from 'lodash/isEmpty';

import Pencil from '@decisiv/iconix/lib/components/Pencil';

import Button from '@decisiv/ui-components/lib/components/Button';

import { useCurrentUserContext } from 'utils/User';
import type { ManagedFleet } from '../..';
import { useCurrentUserPortalUrlsQuery } from '../../hooks/useCurrentUserPortalUrlsQuery';

interface ActionCellProps {
  rowData: ManagedFleet;
}

const dealerGroupRegex = /^Dealer Group \d+$/;

const ActionsCell = ({ rowData: platformAccount }: ActionCellProps) => {
  const { isSuperUser, email: currentUserEmail } = useCurrentUserContext();
  const { data, loading } = useCurrentUserPortalUrlsQuery();

  const targetUrl = useMemo(() => {
    const portalUrls = get(
      data,
      'currentUserProfile.user.portalUrls',
      [],
    ) as DecisivAppUrl[];
    if (isEmpty(portalUrls)) return null;

    const oemId = get(platformAccount, 'oems[0]', '').toLowerCase();
    if (!oemId) return null;

    const matchingPortal = portalUrls.find(
      (portal: { id: string }) => lowerCase(portal.id) === lowerCase(oemId),
    );

    return matchingPortal
      ? `${matchingPortal.url}/dashboard_management/${platformAccount.sourceId}`
      : null;
  }, [data, platformAccount]);

  const isDisabled = useMemo(() => {
    if (loading || isEmpty(targetUrl)) {
      return true;
    }

    // Disable the button for "Dealer Group" platform accounts
    if (dealerGroupRegex.test(platformAccount.name)) {
      return true;
    }

    if (isSuperUser) {
      return false;
    }

    const teamAdmins =
      get(platformAccount, 'sentinelTeam.teamAdmins', []) || [];
    return !teamAdmins.some(
      (admin: { email?: string }) => admin.email === currentUserEmail,
    );
  }, [isSuperUser, platformAccount, currentUserEmail, targetUrl, loading]);

  const handleActionClick = () => {
    if (!targetUrl) return;

    window.open(targetUrl, '_blank');
  };

  return (
    <Button
      text={t`Edit`}
      icon={Pencil}
      kind="secondary"
      size="small"
      onClick={handleActionClick}
      disabled={isDisabled}
      variant={loading ? 'ghost' : undefined}
      loading={loading}
    />
  );
};

export default ActionsCell;
